import { PART_TYPES, SHIRT_COLOR_TYPES, TRANSFORMATION_TYPES } from '../faceParts/data/enums'
import { MAX, MIN, TRANSFORMATION_SLIDER_VALUES } from '../faceParts/data/constants'
import { populate, getAvatarParts, updateHairBack } from '../helpers/avatarHelpers'
import faceData from '../faceParts/data/data'
import initialValues from './initialValues'
import getReducerHandler from '../../../providers/getReducerHandler'
import handleReducerSimpleSetter from '../../../providers/handleReducerSimpleSetter'

// Exported action constants to avoid string literal typos across dispatch sites
export const ACTIONS = Object.freeze({
  RESET_STATE: 'RESET_STATE',
  INIT_DATA: 'INIT_DATA',
  SET_ACTIVE_AVATAR_PARTS: 'SET_ACTIVE_AVATAR_PARTS',
  SET_TRANSFORMATION_TYPE: 'SET_TRANSFORMATION_TYPE',
  SET_SHIRT_COLOR_TYPE: 'SET_SHIRT_COLOR_TYPE',
  TOGGLE_EYE_LINER: 'TOGGLE_EYE_LINER',
  UPDATE_PART_POSITION: 'UPDATE_PART_POSITION',
  SET_COLOR: 'SET_COLOR',
  SET_SCALE: 'SET_SCALE',
  SET_SPACING: 'SET_SPACING',
  SET_VERTICAL_OFFSET: 'SET_VERTICAL_OFFSET',
  SET_ROTATION: 'SET_ROTATION',
})

/*
 * --- INDIVIDUAL ACTION HANDLERS ---
 * (Set multiple values & merge/processing logic only, otherwise use handleReducerSimpleSetter)
 */

const handleResetState = () => ({ ...initialValues })

const handleInitData = (state, action) => {
  const { gender, savedAvatarSpecs } = action.value
  const { partsSections } = populate(gender)
  const avatarPartsConfig = getAvatarParts(savedAvatarSpecs || {})

  // Get the first tab's part type to initialize selectedPart
  const firstPartType = partsSections[0]?.partType
  const firstPart = firstPartType
    ? { ...avatarPartsConfig[firstPartType], partType: firstPartType }
    : { id: '', partType: '' }

  if (savedAvatarSpecs) {
    const faceType = savedAvatarSpecs?.faceShape?.id
    return {
      ...state,
      partsSections,
      avatarPartsConfig,
      faceType,
      selectedTransformationType: '',
      selectedPart: firstPart,
    }
  }

  // we are creating a new avatar; set all the default parts
  partsSections.forEach((sectionData) => {
    const activeAvatarSection = avatarPartsConfig[sectionData.partType]
    if (activeAvatarSection.hasDefaultSelection) {
      activeAvatarSection.id = sectionData.partVarieties[0].data[0].id
    }
  })

  const faceType = partsSections[0].partVarieties[0].data[0].id
  return {
    ...state,
    partsSections,
    avatarPartsConfig,
    faceType,
    selectedTransformationType: '',
    selectedPart: firstPart,
  }
}

const handleSetActiveAvatarParts = (state, action) => {
  const { selectedTransformationType, avatarPartsConfig } = state
  const { part } = action.value
  const { id, partType } = part

  const updatedAvatarPartsConfig = updateHairBack({ avatarPartsConfig, id, partType })
  updatedAvatarPartsConfig[partType] = part

  const faceType = partType === PART_TYPES.FACE_SHAPE ? id : state.faceType
  const sliderMidpoint =
    (TRANSFORMATION_SLIDER_VALUES[selectedTransformationType]?.[MAX] || 0) -
    (TRANSFORMATION_SLIDER_VALUES[selectedTransformationType]?.[MIN] || 0)

  // Only clear selectedTransformationType if we're switching to a different part type
  const newTransformationType = state.selectedPart.partType === partType ? selectedTransformationType : ''

  // Map transformation type to part property name
  let newSliderValue = sliderMidpoint
  if (selectedTransformationType === TRANSFORMATION_TYPES.SCALE) {
    newSliderValue = part.scale || sliderMidpoint
  } else if (selectedTransformationType === TRANSFORMATION_TYPES.ROTATION) {
    newSliderValue = part.rotation || sliderMidpoint
  } else if (selectedTransformationType === TRANSFORMATION_TYPES.SPACING) {
    newSliderValue = part.spacingOffset || sliderMidpoint
  } else if (selectedTransformationType === TRANSFORMATION_TYPES.VERTICAL_OFFSET) {
    newSliderValue = part.verticalOffset || sliderMidpoint
  }

  return {
    ...state,
    avatarPartsConfig: updatedAvatarPartsConfig,
    selectedPart: part,
    selectedSliderValue: newSliderValue,
    selectedTransformationType: newTransformationType,
    faceType,
  }
}

const handleSetTransformationType = (state, action) => {
  let selectedSliderValue = ''

  if (action.value === TRANSFORMATION_TYPES.SCALE) {
    selectedSliderValue = state.selectedPart.scale
  }

  if (action.value === TRANSFORMATION_TYPES.ROTATION) {
    selectedSliderValue = state.selectedPart.rotation
  }

  if (action.value === TRANSFORMATION_TYPES.SPACING) {
    selectedSliderValue = state.selectedPart.spacingOffset
  }

  if (action.value === TRANSFORMATION_TYPES.VERTICAL_OFFSET) {
    selectedSliderValue = state.selectedPart.verticalOffset
  }

  return { ...state, selectedSliderValue, selectedTransformationType: action.value }
}

const handleToggleEyeLiner = (state) => {
  const { avatarPartsConfig } = state
  const { eyes } = avatarPartsConfig
  const { eyeLiner } = eyes
  return {
    ...state,
    avatarPartsConfig: {
      ...avatarPartsConfig,
      eyes: {
        ...eyes,
        eyeLiner: !eyeLiner,
      },
    },
  }
}

const handleUpdatePartPosition = (state, action) => {
  const { partType, id, adjustmentValue, adjustmentKey } = action.value
  const { faceType, originalPositions = {} } = state
  const match = faceData[partType].find((element) => element.id === id)

  if (match?.positioning) {
    const oldPosition = match.positioning

    if (originalPositions[faceType]?.[adjustmentKey] === undefined) {
      originalPositions[faceType] = {
        [adjustmentKey]: oldPosition[adjustmentKey],
      }
    }

    const newValue = originalPositions[faceType][adjustmentKey] + adjustmentValue || 0
    match.positioning[faceType] = {
      ...oldPosition,
      [adjustmentKey]: newValue,
    }
  }
  return { ...state, originalPositions }
}

const handleSetColor = (state, action) => {
  const { color: colorKey, partType } = action.value
  const { selectedShirtColorType, avatarPartsConfig } = state
  const { [partType]: selectedPart, jewelryEarrings, jewelryNecklaces } = avatarPartsConfig || {}

  if (partType === PART_TYPES.SHIRT) {
    if (selectedShirtColorType === SHIRT_COLOR_TYPES.MAIN) {
      selectedPart.colorKey = colorKey
    }
    if (selectedShirtColorType === SHIRT_COLOR_TYPES.SECOND) {
      selectedPart.secondaryColorKey = colorKey
    }
    return { ...state, selectedPart }
  }

  if (partType === PART_TYPES.JEWELRY) {
    jewelryEarrings.colorKey = colorKey
    jewelryNecklaces.colorKey = colorKey
  }
  selectedPart.colorKey = colorKey

  return { ...state, selectedPart }
}

const handleSetScale = (state, action) => {
  state.avatarPartsConfig[state.selectedPart.partType].scale = action.value
  return { ...state, selectedSliderValue: action.value }
}

const handleSetSpacing = (state, action) => {
  if (state.selectedPart.partType === PART_TYPES.EYES) {
    state.avatarPartsConfig.eyes.spacingOffset = action.value
    state.avatarPartsConfig.facialLinesEyes.spacingOffset = action.value
  }
  if (state.selectedPart.partType === PART_TYPES.EYEBROWS) {
    state.avatarPartsConfig.eyebrows.spacingOffset = action.value
  }

  return { ...state, selectedSliderValue: action.value }
}

const handleSetVerticalOffset = (state, action) => {
  const verticalOffsetObj = {}
  if (state.selectedPart.partType === PART_TYPES.GLASSES) {
    state.avatarPartsConfig.glasses.verticalOffset = action.value
  }

  if (state.selectedPart.partType === PART_TYPES.EYES) {
    const delta = state.avatarPartsConfig.eyes.verticalOffset - state.avatarPartsConfig.eyebrows.verticalOffset

    state.avatarPartsConfig.eyes.verticalOffset = action.value
    state.avatarPartsConfig.facialLinesEyes.verticalOffset = action.value
    state.avatarPartsConfig.eyebrows.verticalOffset = action.value - delta
  }

  if (state.selectedPart.partType === PART_TYPES.EYEBROWS) {
    state.avatarPartsConfig.eyebrows.verticalOffset = action.value
  }

  if (state.selectedPart.partType === PART_TYPES.MOUTH) {
    state.avatarPartsConfig.mouth.verticalOffset = action.value
  }

  if (state.selectedPart.partType === PART_TYPES.FACIAL_HAIR) {
    state.avatarPartsConfig.facialHair.verticalOffset = action.value
  }

  if (state.selectedPart.partType === PART_TYPES.NOSE) {
    state.avatarPartsConfig.nose.verticalOffset = action.value
  }

  // if (state.selectedPart.partType === PART_TYPES.JEWELRY_EARRINGS) {
  //   state.avatarPartsConfig.jewelryEarrings.verticalOffset = action.value
  // }

  // if (state.selectedPart.partType === PART_TYPES.JEWELRY_NECKLACES) {
  //   state.avatarPartsConfig.jewelryNecklaces.verticalOffset = action.value
  // }

  return { ...state, ...verticalOffsetObj, selectedSliderValue: action.value }
}

const handleSetRotation = (state, action) => {
  state.avatarPartsConfig[state.selectedPart.partType].rotation = action.value
  return { ...state, selectedSliderValue: action.value }
}

// --- Handlers registry ---
const handlers = {
  [ACTIONS.RESET_STATE]: handleResetState,
  [ACTIONS.INIT_DATA]: handleInitData,
  [ACTIONS.SET_ACTIVE_AVATAR_PARTS]: handleSetActiveAvatarParts,
  [ACTIONS.SET_TRANSFORMATION_TYPE]: handleSetTransformationType,
  [ACTIONS.SET_SHIRT_COLOR_TYPE]: handleReducerSimpleSetter,
  [ACTIONS.TOGGLE_EYE_LINER]: handleToggleEyeLiner,
  [ACTIONS.UPDATE_PART_POSITION]: handleUpdatePartPosition,
  [ACTIONS.SET_COLOR]: handleSetColor,
  [ACTIONS.SET_SCALE]: handleSetScale,
  [ACTIONS.SET_SPACING]: handleSetSpacing,
  [ACTIONS.SET_VERTICAL_OFFSET]: handleSetVerticalOffset,
  [ACTIONS.SET_ROTATION]: handleSetRotation,
}

export default function avatarReducer(state, action) {
  return getReducerHandler(state, action, handlers)
}
